/**
 * Pure calculation/validation logic for `DeductionDrawer` (#22834) --
 * mirrors `allowance_drawer_validation.ts` one-for-one, brought up to the
 * SAME rate-type/amount + rename-preservation parity #22820 built for
 * allowances. `deduction_drawer.tsx` used to collect ONLY a name -- every
 * deduction it created silently became `rate_type: 'fixed'` with no
 * `rate_cents` regardless of user intent, the exact same bug #22820 fixed
 * for `allowance_drawer.tsx` (this drawer was built as a straight mirror of
 * that drawer's OLD, pre-#22820, name-only shape).
 *
 * `payroll.createPayItem`/`updatePayItem`'s zod input
 * (`apps/api/src/trpc/routers/payroll.ts`) applies `rate_type`/`rate_cents`/
 * `rate_bps` with NO `category` restriction -- verified directly against the
 * router, not assumed -- so the exact same rate-type selector + amount/
 * percent field this file's allowance counterpart built applies unchanged
 * to deductions.
 *
 * Owns:
 *  - `DeductionRateKind` -- the drawer's 4-way rate-type selector, a
 *    UI-level superset of the backend's 3-way `rate_type` enum. Declared as
 *    this file's OWN type (not a re-exported alias of
 *    `AllowanceRateKind`) even though the four option values are currently
 *    identical -- see `wagepro_store_data_centre.ts`'s module docblock,
 *    "Reused vs. mirrored" section, for the full extraction judgment call:
 *    the two drawers' option sets are independently-declared UI concerns,
 *    not a shared backend contract, unlike the actual wire-shape plumbing
 *    (`DeductionRateDraft`/`createRateFields`/`updateRateFields` in
 *    `wagepro_store_data_centre.ts`), which genuinely IS reused verbatim.
 *  - `DeductionRateFormState` -- the drawer's raw editable state, structurally
 *    identical to `AllowanceRateFormState` for the same reason
 *    (amount-bearing kinds share one minor-units field, `percentage` keeps a
 *    percent STRING for in-progress keystrokes like `"1."`).
 *  - `deductionRateFormStateFromRule` -- reverse-maps an existing
 *    `Deduction.rule` into editable form state, for edit-mode prefill.
 *  - `isDeductionRateFormValid` / `toDeductionRateDraft` -- validates the
 *    current form state and converts it into the data layer's
 *    `DeductionRateDraft` (`wagepro_store_data_centre.ts`'s alias of
 *    `AllowanceRateDraft`), matching `payroll.createPayItem`/
 *    `updatePayItem`'s zod input exactly.
 *  - `isDeductionRateSectionValid` / `resolveDeductionRateDraft` (#22834,
 *    mirrors #22820 F1/F2) -- the untouched-rate-section escape hatch. A rate
 *    section the user never interacted with (`rateTouched: false`) is always
 *    valid regardless of what `isDeductionRateFormValid` says about its
 *    prefilled amount, and its save resolves to the ORIGINAL
 *    `DeductionRateSnapshot` (`../../data/types.ts`) verbatim via
 *    `DeductionRateDraft`'s `'unchanged'` kind, rather than
 *    `toDeductionRateDraft`'s normal re-derivation from the current (merely
 *    prefilled) form state. Unlike allowances (where this was a retrofit an
 *    adversarial review caught post-ship), the deduction drawer gets this
 *    fix from day one -- see `wagepro_store_data_centre.ts`'s module
 *    docblock, "Untouched rate section round-trips unchanged" section, for
 *    the full rationale this closes.
 */
import type { DeductionRateSnapshot, DeductionRule } from '../../data/types';
import type { DeductionRateDraft } from '../../data/wagepro_store_data_centre';
import {
  formatBasisPointsAsPercentInput,
  parsePercentToBasisPoints,
} from '../../data/wagepro_store_payroll_settings';

export type DeductionRateKind =
  | 'fixed'
  | 'per_hour'
  | 'percentage'
  | 'manager_input';

/** Selector option order, top to bottom -- mirrors `ALLOWANCE_RATE_KINDS`. */
export const DEDUCTION_RATE_KINDS: readonly DeductionRateKind[] = [
  'fixed',
  'per_hour',
  'percentage',
  'manager_input',
];

/**
 * Matches `payroll.createPayItem`/`updatePayItem`'s `rate_bps` ceiling
 * exactly (`z.number().int().min(0).max(100_000)`,
 * `apps/api/src/trpc/routers/payroll.ts`) -- mirrors
 * `allowance_drawer_validation.ts`'s own `MAX_RATE_BPS`. Duplicated (rather
 * than imported from that file) so this file stays self-contained and
 * independently testable -- see `wagepro_store_data_centre.ts`'s "Reused vs.
 * mirrored" note for why the two drawers' validation layers are mirrored,
 * not shared.
 */
export const MAX_RATE_BPS = 100_000;

export interface DeductionRateFormState {
  rateKind: DeductionRateKind;
  /** Minor units (cents). Only meaningful when `rateKind` is `'fixed'`/`'per_hour'`. */
  amountCents: number;
  /** Raw percent text (e.g. `"8.5"`). Only meaningful when `rateKind` is `'percentage'`. */
  ratePercentText: string;
}

export function defaultDeductionRateFormState(): DeductionRateFormState {
  return { rateKind: 'manager_input', amountCents: 0, ratePercentText: '0' };
}

/**
 * Reverse-maps an existing deduction's derived `rule` into editable form
 * state, for edit-mode prefill. `progressive_bracket` is never produced by
 * the real read adapter (see `wagepro_store_data_centre.ts`'s module
 * docblock -- no statutory `pay_items` row exists to derive it from) so it
 * falls back to the same safe "no rate recorded" default `manager_input`
 * uses, rather than fabricating a value.
 */
export function deductionRateFormStateFromRule(
  rule: DeductionRule,
): DeductionRateFormState {
  switch (rule.kind) {
    case 'fixed_installment':
      return {
        rateKind: 'fixed',
        amountCents: rule.amount,
        ratePercentText: '0',
      };
    case 'hourly_rate':
      return {
        rateKind: 'per_hour',
        amountCents: rule.amount,
        ratePercentText: '0',
      };
    case 'percent_of_basic':
      return {
        rateKind: 'percentage',
        amountCents: 0,
        ratePercentText: formatBasisPointsAsPercentInput(rule.rate),
      };
    case 'manager_input':
    case 'progressive_bracket':
      return defaultDeductionRateFormState();
  }
}

/**
 * Whether the current form state has everything needed to save: an
 * amount-bearing rate type needs a positive amount/rate entered (a
 * zero/blank value is treated as "not yet entered", not a valid zero-rate
 * deduction). `manager_input` never needs a value. Mirrors
 * `isAllowanceRateFormValid` exactly.
 */
export function isDeductionRateFormValid(
  state: DeductionRateFormState,
): boolean {
  switch (state.rateKind) {
    case 'fixed':
    case 'per_hour':
      return state.amountCents > 0;
    case 'percentage': {
      const bps = parsePercentToBasisPoints(state.ratePercentText);
      return bps !== null && bps > 0 && bps <= MAX_RATE_BPS;
    }
    case 'manager_input':
      return true;
  }
}

/**
 * Converts a validated form state into the data layer's `DeductionRateDraft`.
 * Callers must gate on {@link isDeductionRateFormValid} first (mirrored here
 * by a `?? 0` fallback so an in-progress invalid percent string can never
 * produce `NaN`/`undefined` on the wire).
 */
export function toDeductionRateDraft(
  state: DeductionRateFormState,
): DeductionRateDraft {
  switch (state.rateKind) {
    case 'fixed':
      return { kind: 'fixed', rate_cents: state.amountCents };
    case 'per_hour':
      return { kind: 'per_hour', rate_cents: state.amountCents };
    case 'percentage':
      return {
        kind: 'percentage',
        rate_bps: parsePercentToBasisPoints(state.ratePercentText) ?? 0,
      };
    case 'manager_input':
      return { kind: 'manager_input' };
  }
}

/**
 * Whether Save should be blocked on the rate section (#22834, mirrors
 * #22820 F2). A rate section the user has not touched since the drawer
 * opened is ALWAYS valid, no matter what {@link isDeductionRateFormValid}
 * says about its prefilled amount -- an existing record with an explicit
 * `rate_cents: 0` (or `rate_bps: 0`/`null`) prefills into an amount-bearing
 * kind with a technically-invalid zero amount, and that must never block a
 * pure rename. Once touched, falls back to the normal rule: the user is
 * actively choosing a rate now, so it has to be a valid one.
 */
export function isDeductionRateSectionValid(
  state: DeductionRateFormState,
  rateTouched: boolean,
): boolean {
  if (!rateTouched) return true;
  return isDeductionRateFormValid(state);
}

/**
 * Resolves the `DeductionRateDraft` a save actually sends (#22834, mirrors
 * #22820 F1): an untouched rate section on an EDIT (a real
 * `originalSnapshot` present) sends that ORIGINAL triad back verbatim via the
 * `'unchanged'` kind -- never re-derived from `state`, which is only the
 * as-loaded prefill and has already lost the fixed-vs-per_hour distinction
 * for a null-rate record (see `DeductionRateSnapshot`'s doc comment,
 * `../../data/types.ts`). Falls back to the normal `toDeductionRateDraft`
 * mapping whenever the section HAS been touched, or there is no original to
 * preserve (a brand-new deduction, where `originalSnapshot` is `undefined`
 * because there is nothing to round-trip).
 */
export function resolveDeductionRateDraft(
  state: DeductionRateFormState,
  rateTouched: boolean,
  originalSnapshot: DeductionRateSnapshot | undefined,
): DeductionRateDraft {
  if (!rateTouched && originalSnapshot !== undefined) {
    return {
      kind: 'unchanged',
      rate_type: originalSnapshot.rate_type,
      rate_cents: originalSnapshot.rate_cents,
      rate_bps: originalSnapshot.rate_bps,
    };
  }
  return toDeductionRateDraft(state);
}
